import { useEffect, useState } from "react";
import EntityQuickFacts from "./EntityQuickFacts";
import api from "../lib/api";

/* Shared dark-hero shell used by /airports/:code, /countries/:code,
   /destinations/:id. Handles:
   - background image with tinted overlay (`tone` prop)
   - if no image is provided, auto-fetches a matching Unsplash photo via
     `unsplashQuery` (e.g. `${entity.name} skyline`) — silently falls back
     to no photo if the API is unreachable
   - breadcrumbs + eyebrow tags + optional mono `code`
   - serif h1 title + subtitle + action row (children slot)
   - optional attached `tabsAbove` (rendered before the hero band)
   - Quick Facts strip anchored just below the hero with -mt-8 lift */

const OVERLAYS = {
  navy: "bg-primary/85",
  blue: "bg-blue-900/80",
  violet: "bg-violet-900/75",
  cyan: "bg-cyan-800/75",
  emerald: "bg-emerald-900/75",
  rose: "bg-rose-900/75",
};

export default function EntityHero({
  image,
  unsplashQuery,     // used only when `image` is falsy
  tone = "navy",
  breadcrumbs,
  tags,
  code,
  title,
  subtitle,
  actions,
  quickFacts,
  tabsAbove,
  children,
  testid = "entity-hero",
}) {
  const [autoImage, setAutoImage] = useState(null);
  useEffect(() => {
    if (image || !unsplashQuery) { setAutoImage(null); return; }
    let ignore = false;
    api.get("/unsplash/search", { params: { q: unsplashQuery, per_page: 1 } })
      .then((r) => {
        if (ignore) return;
        const first = (r.data?.results || r.data?.items || [])[0];
        const src = first?.url_regular || first?.urls?.regular || first?.image_url || first?.url;
        if (src) setAutoImage(src);
      })
      .catch(() => { /* silent — pages render with tinted background */ });
    return () => { ignore = true; };
  }, [image, unsplashQuery]);
  const bg = image || autoImage;
  const overlay = OVERLAYS[tone] || OVERLAYS.navy;
  return (
    <div data-testid={testid}>
      {tabsAbove}
      <section className="relative border-b border-border overflow-hidden" data-testid={`${testid}-band`}>
        {bg && (
          <div className="absolute inset-0">
            <img src={bg} alt="" className="w-full h-full object-cover" loading="eager" />
            <div className={`absolute inset-0 ${overlay}`} aria-hidden="true" />
          </div>
        )}
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-12 sm:py-14">
          {breadcrumbs && <div className="text-white/90 mb-3" data-testid={`${testid}-breadcrumbs`}>{breadcrumbs}</div>}
          {(tags || code) && (
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={`${testid}-tags`}>
              {code && <span className="font-mono text-2xl text-white/95 font-600">{code}</span>}
              {tags}
            </div>
          )}
          <h1 data-testid={`${testid}-title`}
            className="font-serif font-800 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight max-w-3xl">
            {title}
          </h1>
          {subtitle && (
            <div data-testid={`${testid}-subtitle`} className="mt-3 text-white/90 text-base sm:text-lg font-500 max-w-3xl leading-relaxed">
              {subtitle}
            </div>
          )}
          {actions && (
            <div data-testid={`${testid}-actions`} className="mt-4 flex flex-wrap items-center gap-2">
              {actions}
            </div>
          )}
          {children}
        </div>
      </section>
      {quickFacts && quickFacts.length > 0 && (
        <div className="max-w-7xl mx-auto px-5 lg:px-8 -mt-8 relative z-10">
          <EntityQuickFacts facts={quickFacts} testid={`${testid}-facts`} />
        </div>
      )}
    </div>
  );
}
